import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { canScheduleTicket } from 'utils/reScreening';
import NewHomePageLayout from 'components/Common/Layouts/NewHomePageLayout';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import LoginWizard from 'components/Wizard/LoginWizard';
import TrialNoticeBanner from 'components/Common/TrialNoticeBanner';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {
  useGetComplianceHealthByGroupId,
  useGetEntityQualityMetricsByGroupId,
  useGetStatsByGroupId,
  useGetGroupRecentActivityStats,
} from 'api/bitedb/stats/stats.hooks';
import { useGetScreeningResultsForGroup } from 'api/bitedb/screeningHistory/screening.hooks';
import { useGetDatabasesByGroupId } from 'api/bitedb/database/database.hooks';
import { useGetEntitiesByGroupId } from 'api/bitedb/entities/entities.hooks';
import { PaginationParams } from 'api/bitedb/entities/entities';
import CasesAPI from 'api/CasesAPI';
import { getDateDifference } from 'utils/dateFormater';
import {
  isMissingActiveTeamSelection,
  requiresTeamOnboarding,
} from 'utils/teamOnboarding';
import 'resources/styles/homepage-redesign.css';
import SearchComp from 'components/Homepage/SearchComp';
import { GroupOutlined } from '@mui/icons-material';
import { getDatabaseStatsBatch } from 'api/bitedb/stats/stats.service';
import { getTariffDatabaseSummariesBatch } from 'api/bitedb/tariffs/tariffs.service';

interface KpiCard {
  icon: React.ReactNode;
  iconTone: 'green' | 'blue' | 'amber' | 'slate';
  value: string;
  title: string;
  delta: string;
  positive: boolean;
}

interface DatabaseCard {
  title: string;
  description: string;
  total: string;
  totalLabel: string;
  qualityLabel: string;
  quality: number;
  delta: string;
  growth: string;
  tab: 'Products' | 'People & Companies';
}

interface StartCard {
  icon: React.ReactNode;
  iconTone: 'blue' | 'green' | 'amber';
  title: string;
  subtitle: string;
  items: { label: string; to: string; disabled?: boolean }[];
}

interface ActivityRow {
  title: string;
  label: string;
  iconKey: string;
  detail: string;
  status: string;
  statusTone: 'success' | 'warning' | 'danger';
  time: string;
  viewTo?: string;
}

interface TariffAnalyzerSummary {
  totalValue: number;
  totalDuty: number;
  requiresReview: number;
}

interface DatabaseSummary {
  flaggedEntities?: number;
  reviewQueueEntities?: number;
  totalComplianceScore?: number;
  totalIssues?: number;
  totalScreeningResults?: number;
}

interface SavedWorkTicket {
  _id?: string;
  ticketName?: string;
  createdAt?: string;
  lastUpdate?: string;
  docType?: string;
  tariffJurisdiction?: string;
  status?: string;
  frequency?: string;
  curTotal?: number;
  curNew?: number;
  entity?: {
    primary?: string | string[];
    country?: string;
    address?: string;
  };
  commodity?: {
    harmonizedScheduleCode?: string;
    commodityName?: string;
    countryOfOrigin?: string;
    destinationCountry?: string;
    eccn?: string;
  };
  classificationValue?: string;
  eccnResults?: string[] | string;
  aiSums?: Array<{
    risk?: string;
    risk_level?: string;
    burden?: string;
  }>;
  workflowData?: {
    country_screening?: {
      input_destination_country?: string;
      normalized_country_name?: string;
    };
  };
  activityType?: string;
  workflowType?: string;
  results?: {
    allTotal?: number;
  };
  tariffResults?: unknown;
}

interface SignalCard {
  icon: React.ReactNode;
  iconTone: 'amber' | 'blue' | 'slate' | 'red';
  value: string;
  title: string;
  subtitle: string;
  footer: string;
  footerTone: 'blue' | 'orange' | 'red';
}

const kpiCards: KpiCard[] = [
  {
    icon: <SecurityOutlinedIcon fontSize='small' />,
    iconTone: 'green',
    value: '94%',
    title: 'Product Compliance Health',
    delta: '+2.1% vs last month',
    positive: true,
  },
  {
    icon: <TrendingUpOutlinedIcon fontSize='small' />,
    iconTone: 'blue',
    value: '4,821',
    title: 'Imports and Exports Analyzed',
    delta: '+12% vs last month',
    positive: true,
  },
  {
    icon: <WarningAmberOutlinedIcon fontSize='small' />,
    iconTone: 'amber',
    value: '23',
    title: 'High Risk Entity Matches',
    delta: '+5 this week',
    positive: true,
  },
  {
    icon: <MonitorHeartOutlinedIcon fontSize='small' />,
    iconTone: 'slate',
    value: '102',
    title: 'Runs This Week',
    delta: '-8 vs last week',
    positive: false,
  },
];

const databaseCards: DatabaseCard[] = [
  {
    title: 'Product Database',
    description: 'Canonical product records with HTS classifications',
    total: '12,487',
    totalLabel: 'Products',
    qualityLabel: 'Product Compliance Health',
    quality: 92,
    delta: '+34 this week',
    growth: '2.1% growth',
    tab: 'Products',
  },
  {
    title: 'Entity Database',
    description: 'Suppliers, consignees, and trade partners',
    total: '3,241',
    totalLabel: 'Entities',
    qualityLabel: 'Entities Reviewed',
    quality: 87,
    delta: '+8 this week',
    growth: '1.4% growth',
    tab: 'People & Companies',
  },
];

const startCards: StartCard[] = [
  {
    icon: <BuildOutlinedIcon fontSize='small' />,
    iconTone: 'blue',
    title: 'Compliance Tools',
    subtitle: 'Single-operation utilities for quick compliance tasks.',
    items: [
      { label: 'Entity Screen', to: '/entity-screening' },
      { label: 'Classify', to: '/bite-classify' },
      { label: 'Tariff Calculator', to: '/tariff-checker' },
      { label: 'Import Screen', to: '/import-screening' },
      { label: 'Export Screen', to: '/export-screening' },
      { label: 'ECCN Check', to: '/bite-eccn' },
    ],
  },
  {
    icon: <TroubleshootOutlinedIcon fontSize='small' />,
    iconTone: 'green',
    title: 'Run an Analyzer',
    subtitle: 'Bulk intelligence across your data.',
    items: [
      { label: 'ACE Analyzer', to: '/ace-analyzers' },
      { label: 'Tariff Analyzer', to: '/tariff-analyzers' },
      { label: 'AES Analyzer', to: '/aes-analyzers' },
    ],
  },
  {
    icon: <RouteOutlinedIcon fontSize='small' />,
    iconTone: 'amber',
    title: 'Start a Workflow',
    subtitle: 'Agentic multi-step automation runs.',
    items: [
      { label: 'Import Workflow', to: '/import-page' },
      { label: 'Export Workflow', to: '/export-page' },
      { label: 'Design Assistant', to: '/export-design' },
    ],
  },
];

const signals: SignalCard[] = [
  {
    icon: <WarningAmberOutlinedIcon fontSize='small' />,
    iconTone: 'amber',
    value: '14',
    title: 'Entities Flagged This Week',
    subtitle: 'Requires sanctions review',
    footer: '+3 from last week',
    footerTone: 'orange',
  },
  {
    icon: <TrendingUpOutlinedIcon fontSize='small' />,
    iconTone: 'blue',
    value: '0',
    title: 'Products Impacted by Tariff Updates',
    subtitle: 'HTS codes updated by CBP',
    footer: 'New USTR list published',
    footerTone: 'blue',
  },
  {
    icon: <DescriptionOutlinedIcon fontSize='small' />,
    iconTone: 'slate',
    value: '0',
    title: 'New CBP Rulings',
    subtitle: 'Published this week',
    footer: '2 relevant to your products',
    footerTone: 'blue',
  },
  {
    icon: <SecurityOutlinedIcon fontSize='small' />,
    iconTone: 'red',
    value: '0',
    title: 'Active Risk Alerts',
    subtitle: 'High-priority review required',
    footer: '2 open since 7+ days',
    footerTone: 'red',
  },
];

const statusClass: Record<ActivityRow['statusTone'], string> = {
  success: 'home-v3-status-success',
  warning: 'home-v3-status-warning',
  danger: 'home-v3-status-danger',
};

const getActivityStatusTone = (
  status: string | undefined,
): ActivityRow['statusTone'] => {
  const normalized = String(status || 'Completed').toLowerCase();

  if (
    normalized.includes('reject') ||
    normalized.includes('fail') ||
    normalized.includes('error')
  ) {
    return 'danger';
  }

  if (
    normalized.includes('pending') ||
    normalized.includes('review') ||
    normalized.includes('progress')
  ) {
    return 'warning';
  }

  return 'success';
};

const isWorkflowTicket = (ticket: SavedWorkTicket) => {
  if (ticket.activityType === 'workflow' || ticket.workflowType) {
    return true;
  }

  if (ticket.docType === 'Export Unified' && ticket.workflowData) {
    return true;
  }

  if (ticket.docType !== 'Import Unified') {
    return false;
  }

  const hasTariffSnapshot = Boolean(ticket.tariffResults);
  const hasWorkflowScreeningShape =
    typeof ticket.curTotal === 'number' &&
    Boolean(ticket.frequency) &&
    !hasTariffSnapshot &&
    Boolean(
      ticket.commodity?.countryOfOrigin ||
        ticket.results?.allTotal ||
        ticket.commodity?.destinationCountry,
    );

  return hasWorkflowScreeningShape;
};

const formatWorkflowDetail = (ticket: SavedWorkTicket) => {
  const workflowType =
    ticket.workflowType ||
    (ticket.docType === 'Export Unified' ? 'export' : 'import');

  const primaryCode =
    workflowType === 'export'
      ? ticket.commodity?.eccn || ticket.commodity?.harmonizedScheduleCode
      : ticket.commodity?.harmonizedScheduleCode;

  const primaryLabel =
    primaryCode && workflowType === 'export'
      ? `ECCN ${primaryCode}`
      : primaryCode
      ? `HTS ${primaryCode}`
      : ticket.commodity?.commodityName || 'Workflow run';

  const origin =
    workflowType === 'export'
      ? ticket.workflowData?.country_screening?.normalized_country_name ||
        ticket.workflowData?.country_screening?.input_destination_country ||
        ticket.commodity?.destinationCountry
      : ticket.commodity?.countryOfOrigin;

  const destination = ticket.commodity?.destinationCountry;
  const route =
    workflowType === 'import'
      ? [origin, destination].filter(Boolean).join(' -> ')
      : origin || destination || '';

  const totalResults =
    typeof ticket.curTotal === 'number'
      ? ticket.curTotal
      : ticket.results?.allTotal;
  const newResults = typeof ticket.curNew === 'number' ? ticket.curNew : 0;

  const detailParts = [primaryLabel];

  if (route) {
    detailParts.push(route);
  }

  if (typeof totalResults === 'number') {
    detailParts.push(`${totalResults} result${totalResults === 1 ? '' : 's'}`);
  }

  if (newResults > 0) {
    detailParts.push(`${newResults} new`);
  } else if (
    ticket.frequency &&
    ticket.frequency !== 'Once' &&
    canScheduleTicket(ticket.docType, ticket.tariffJurisdiction)
  ) {
    detailParts.push(ticket.frequency);
  }

  return detailParts.join(' · ');
};

const formatToolDetail = (ticket: SavedWorkTicket) => {
  const docType = ticket.docType || '';
  const frequencyDetail =
    ticket.frequency &&
    ticket.frequency !== 'Once' &&
    canScheduleTicket(ticket.docType, ticket.tariffJurisdiction)
      ? ticket.frequency
      : '';
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);

  if (docType === 'Person / Company') {
    const entityPrimary = Array.isArray(ticket.entity?.primary)
      ? ticket.entity?.primary.filter(Boolean).join(', ')
      : ticket.entity?.primary;
    const totalResults =
      typeof ticket.curTotal === 'number'
        ? `${ticket.curTotal} result${ticket.curTotal === 1 ? '' : 's'} found`
        : '';
    const evaluatedRisk =
      ticket.aiSums?.[0]?.risk || ticket.aiSums?.[0]?.risk_level || '';

    return [
      entityPrimary,
      ticket.entity?.country,
      totalResults,
      evaluatedRisk ? `Risk ${evaluatedRisk}` : '',
      frequencyDetail,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (
    docType === 'Import Product Screen' ||
    docType === 'Export Product Screen'
  ) {
    const screenResults =
      typeof ticket.curTotal === 'number'
        ? ticket.curTotal
        : ticket.results?.allTotal;
    const screeningRisk =
      ticket.aiSums?.[0]?.risk_level || ticket.aiSums?.[0]?.risk || '';
    const screeningBurden = ticket.aiSums?.[0]?.burden || '';
    const route = [
      ticket.commodity?.countryOfOrigin,
      ticket.commodity?.destinationCountry,
    ]
      .filter(Boolean)
      .join(' -> ');
    return [
      ticket.commodity?.harmonizedScheduleCode
        ? `HTS ${ticket.commodity.harmonizedScheduleCode}`
        : ticket.commodity?.commodityName,
      route,
      typeof screenResults === 'number'
        ? `${screenResults} result${screenResults === 1 ? '' : 's'}`
        : '',
      screeningRisk ? `Risk ${screeningRisk}` : '',
      screeningBurden ? `Burden ${screeningBurden}` : '',
      frequencyDetail,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (docType === 'Tariff') {
    const tariffTotalCost =
      typeof (ticket.tariffResults as any)?.math?.totalCost === 'number'
        ? (ticket.tariffResults as any).math.totalCost
        : null;
    const tariffDutyCost =
      typeof (ticket.tariffResults as any)?.math?.dutyCost === 'number'
        ? (ticket.tariffResults as any).math.dutyCost
        : null;

    return [
      ticket.commodity?.harmonizedScheduleCode
        ? `HTS ${ticket.commodity.harmonizedScheduleCode}`
        : ticket.commodity?.commodityName,
      ticket.commodity?.countryOfOrigin,
      tariffDutyCost !== null
        ? `Total Duty Cost ${formatCurrency(tariffDutyCost)}`
        : '',
      tariffTotalCost !== null
        ? `Total ${formatCurrency(tariffTotalCost)}`
        : '',
      frequencyDetail,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (docType === 'Classification') {
    return [
      ticket.commodity?.harmonizedScheduleCode
        ? `HTS ${ticket.commodity.harmonizedScheduleCode}`
        : '',
      ticket.commodity?.commodityName || ticket.classificationValue,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (docType === 'ECCN') {
    return [
      ticket.commodity?.eccn ? `ECCN ${ticket.commodity.eccn}` : '',
      ticket.commodity?.commodityName || ticket.classificationValue,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (docType === 'Control Checker' || docType === 'ECCN Check') {
    return [
      ticket.commodity?.eccn ? `ECCN ${ticket.commodity.eccn}` : '',
      ticket.commodity?.destinationCountry,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return 'Ticket created';
};

const formatDBDetail = (activity: any) => {
  const totalCount = Number(activity?.totalCount ?? 0);

  const countLabel =
    activity.activitytype === 'Entity DB'
      ? `${totalCount.toLocaleString()} entit${totalCount === 1 ? 'y' : 'ies'}`
      : activity.activitytype === 'Product DB'
      ? `${totalCount.toLocaleString()} product${totalCount === 1 ? '' : 's'}`
      : `${totalCount.toLocaleString()} line${totalCount === 1 ? '' : 's'}`;

  return [
    totalCount > 0 ? countLabel : '',
    activity.activitytype === 'Entity DB'
      ? `${Number(
          activity?.reviewqueueentities ?? 0,
        ).toLocaleString()} in review queue`
      : '',
    activity.activitytype === 'Entity DB'
      ? `${Number(activity?.flaggedentities ?? 0).toLocaleString()} flagged`
      : '',
    activity.activitytype === 'Product DB' &&
    Number.isFinite(Number(activity?.totalcomplianceccore ?? 0))
      ? `Compliance Score ${Number(activity?.totalcomplianceccore ?? 0).toFixed(
          1,
        )}`
      : '',
    activity.activitytype === 'Product DB' &&
    Number(activity?.totalissues ?? 0) > 0
      ? `${Number(activity?.totalissues ?? 0).toLocaleString()} issue${
          Number(activity?.totalissues ?? 0) === 1 ? '' : 's'
        }`
      : '',
    Number(activity?.totalscreeningresults ?? 0) > 1 ? 'Rescreened' : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

const getActivityLabel = (item: {
  docType?: string;
  workflowType?: string;
  analyzerType?: string;
  type?: string;
}) => {
  if (item.docType) {
    return item.docType;
  }

  if (item.workflowType === 'import') {
    return 'Import Workflow';
  }

  if (item.workflowType === 'export') {
    return 'Export Workflow';
  }

  if (item.analyzerType === 'ACE') {
    return 'ACE Analyzer';
  }

  if (item.type === 'tariffs') {
    return 'Tariff Analyzer';
  }

  if (item.type === 'people & companies') {
    return 'Entity Database';
  }

  if (item.type === 'products') {
    return 'Product Database';
  }

  return 'Recent Activity';
};

const getActivityIcon = (iconKey: string) => {
  switch (iconKey) {
    case 'Import Workflow':
      return <DownloadOutlinedIcon fontSize='small' />;
    case 'Export Workflow':
      return <PublishOutlinedIcon fontSize='small' />;
    case 'Person / Company':
      return <PersonSearchOutlinedIcon fontSize='small' />;
    case 'Classification':
      return <CategoryOutlinedIcon fontSize='small' />;
    case 'Tariff':
      return <PriceCheckOutlinedIcon fontSize='small' />;
    case 'Import Product Screen':
      return <TravelExploreOutlinedIcon fontSize='small' />;
    case 'Export Product Screen':
      return <ManageSearchOutlinedIcon fontSize='small' />;
    case 'Control Checker':
    case 'ECCN Check':
    case 'ECCN':
      return <VerifiedOutlinedIcon fontSize='small' />;
    case 'Multiple Entries':
      return <DescriptionOutlinedIcon fontSize='small' />;
    case 'ACE Analyzer':
      return <TroubleshootOutlinedIcon fontSize='small' />;
    case 'Tariff Analyzer':
      return <AnalyticsOutlinedIcon fontSize='small' />;
    case 'Entity Database':
      return <GroupOutlinedIcon fontSize='small' />;
    case 'Product Database':
      return <Inventory2OutlinedIcon fontSize='small' />;
    case 'BITE DB':
      return <DnsOutlinedIcon fontSize='small' />;
    case 'WORKFLOW':
      return <RouteOutlinedIcon fontSize='small' />;
    case 'TOOL':
      return <BuildOutlinedIcon fontSize='small' />;
    case 'ANALYZER':
      return <TroubleshootOutlinedIcon fontSize='small' />;
    default:
      return <AnalyticsOutlinedIcon fontSize='small' />;
  }
};

const NewHomeDashboardPage: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const selectedTeamId =
    cur_user?.selected_team?.id && cur_user.selected_team.id !== 'EMPTY_TEAM'
      ? cur_user.selected_team.id
      : '';

  const { data: stats } = useGetStatsByGroupId(selectedTeamId);
  const { data: recentActivity } =
    useGetGroupRecentActivityStats(selectedTeamId);
  const { data: entityQualityMetrics } =
    useGetEntityQualityMetricsByGroupId(selectedTeamId);
  const { data: complianceHealthStats } =
    useGetComplianceHealthByGroupId(selectedTeamId);
  const { data: screeningResultsForGroup } =
    useGetScreeningResultsForGroup(selectedTeamId);
  const { allData: allDatabases } = useGetDatabasesByGroupId(selectedTeamId);
  const weekStartIso = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = (day + 6) % 7;
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - diffToMonday);
    return now.toISOString();
  }, []);
  const [showTrialReadyStep, setShowTrialReadyStep] = useState(false);
  const [savedWorkTickets, setSavedWorkTickets] = useState<any[]>([]);
  const [aceRuns] = useState<any[]>([]);
  const [, setTariffAnalyzerSummaries] = useState<
    Record<string, TariffAnalyzerSummary>
  >({});
  const [, setDatabaseSummaries] = useState<Record<string, DatabaseSummary>>(
    {},
  );
  const totalHighRiskParams = useMemo<PaginationParams>(
    () => ({
      page: 1,
      limit: 1,
      sortBy: 'created_at',
      sortOrder: 'desc',
      riskFilters: ['High'],
    }),
    [],
  );
  const weeklyHighRiskParams = useMemo<PaginationParams>(
    () => ({
      page: 1,
      limit: 1,
      sortBy: 'created_at',
      sortOrder: 'desc',
      riskFilters: ['High'],
      createdSince: weekStartIso,
    }),
    [weekStartIso],
  );
  const { pagination: totalHighRiskPagination } = useGetEntitiesByGroupId(
    selectedTeamId,
    '',
    totalHighRiskParams,
  );
  const { pagination: weeklyHighRiskPagination } = useGetEntitiesByGroupId(
    selectedTeamId,
    '',
    weeklyHighRiskParams,
  );

  const getSafeNumber = (value: number | undefined | null) => {
    const safeValue = Number(value);
    return Number.isFinite(safeValue) ? safeValue : 0;
  };

  const formatGrowth = (value: number | undefined | null) => {
    const safeValue = getSafeNumber(value);
    return `${safeValue.toFixed(1)}% growth`;
  };

  const formatCount = (value: number | undefined | null) =>
    getSafeNumber(value).toLocaleString();

  const formatWholePercent = (value: number | undefined | null) =>
    `${Math.round(getSafeNumber(value))}%`;

  const formatSignedPercentDelta = (value: number | undefined | null) => {
    const safeValue = getSafeNumber(value);
    if (safeValue > 0) {
      return `+${safeValue.toFixed(1)}% vs last month`;
    }
    if (safeValue < 0) {
      return `${safeValue.toFixed(1)}% vs last month`;
    }
    return '0.0% vs last month';
  };

  const complianceHealthPercentage = useMemo(
    () =>
      Math.max(
        0,
        Math.min(
          100,
          Math.round(
            Number(
              complianceHealthStats?.current_score ??
                stats?.total_compliance_score_products ??
                0,
            ),
          ),
        ),
      ),
    [
      complianceHealthStats?.current_score,
      stats?.total_compliance_score_products,
    ],
  );

  const dynamicDatabaseCards = databaseCards.map((db) => {
    if (db.tab === 'Products') {
      return {
        ...db,
        total: formatCount(stats?.total_products),
        totalLabel: 'Total Products',
        quality: complianceHealthPercentage,
        delta:
          stats?.products_added_this_week === null
            ? '0'
            : `+${stats?.products_added_this_week} this week`,
        growth: formatGrowth(stats?.products_growth_pct),
      };
    }

    if (db.tab === 'People & Companies') {
      const computedEntityQuality = Math.max(
        0,
        Math.min(
          100,
          Math.round(
            Number(entityQualityMetrics?.data_quality_pct ?? db.quality),
          ),
        ),
      );

      return {
        ...db,
        total: formatCount(stats?.total_entities),
        totalLabel: 'Total Entities',
        quality: computedEntityQuality,
        delta:
          stats?.entities_added_this_week === null
            ? '0'
            : `+${stats?.entities_added_this_week} this week`,
        growth: formatGrowth(stats?.entities_growth_pct),
      };
    }

    return db;
  });

  const aceFindingsTotal = useMemo(
    () =>
      aceRuns.reduce((sum, run) => sum + getSafeNumber(run?.findings_count), 0),
    [aceRuns],
  );

  const dynamicKpiCards = useMemo(
    () =>
      kpiCards.map((card) =>
        card.title === 'Product Compliance Health'
          ? {
              ...card,
              value: formatWholePercent(
                complianceHealthStats?.current_score ??
                  stats?.total_compliance_score_products,
              ),
              delta: formatSignedPercentDelta(
                complianceHealthStats?.change_pct_points ??
                  stats?.products_growth_pct,
              ),
              positive:
                Number(
                  complianceHealthStats?.change_pct_points ??
                    stats?.products_growth_pct ??
                    0,
                ) >= 0,
            }
          : card.title === 'Imports and Exports Analyzed'
          ? {
              ...card,
              value: formatCount(
                aceFindingsTotal + getSafeNumber(stats?.total_tariffs),
              ),
              delta: `ACE ${formatCount(aceFindingsTotal)} · HTS ${formatCount(
                stats?.total_tariffs,
              )}`,
              positive: true,
            }
          : card.title === 'High Risk Entity Matches'
          ? {
              ...card,
              value: formatCount(totalHighRiskPagination?.totalItems),
              delta:
                weeklyHighRiskPagination?.totalItems === null
                  ? '0'
                  : Number(weeklyHighRiskPagination?.totalItems) > 0
                  ? `+${Number(
                      weeklyHighRiskPagination?.totalItems,
                    ).toLocaleString()} flagged this week`
                  : '0 flagged this week',
            }
          : card.title === 'Runs This Week'
          ? {
              ...card,
              value: (() => {
                if (!screeningResultsForGroup?.length) {
                  return '0';
                }
                const weekStart = new Date(weekStartIso).getTime();
                return Number(
                  screeningResultsForGroup.filter((result) => {
                    if (!result?.screening_timestamp) {
                      return false;
                    }
                    const timestamp = new Date(
                      result.screening_timestamp,
                    ).getTime();
                    return !Number.isNaN(timestamp) && timestamp >= weekStart;
                  }).length,
                ).toLocaleString();
              })(),
              delta: (() => {
                if (!screeningResultsForGroup?.length) {
                  return '0 vs last week';
                }
                const weekStart = new Date(weekStartIso).getTime();
                const lastWeekStart = weekStart - 7 * 24 * 60 * 60 * 1000;

                const thisWeekRuns = screeningResultsForGroup.filter(
                  (result) => {
                    if (!result?.screening_timestamp) {
                      return false;
                    }
                    const timestamp = new Date(
                      result.screening_timestamp,
                    ).getTime();
                    return !Number.isNaN(timestamp) && timestamp >= weekStart;
                  },
                ).length;

                const lastWeekRuns = screeningResultsForGroup.filter(
                  (result) => {
                    if (!result?.screening_timestamp) {
                      return false;
                    }
                    const timestamp = new Date(
                      result.screening_timestamp,
                    ).getTime();
                    return (
                      !Number.isNaN(timestamp) &&
                      timestamp >= lastWeekStart &&
                      timestamp < weekStart
                    );
                  },
                ).length;

                const diff = thisWeekRuns - lastWeekRuns;
                if (diff > 0) {
                  return `+${diff.toLocaleString()} vs last week`;
                }
                if (diff < 0) {
                  return `${diff.toLocaleString()} vs last week`;
                }
                return 'No change vs last week';
              })(),
              positive: (() => {
                if (!screeningResultsForGroup?.length) {
                  return false;
                }
                const weekStart = new Date(weekStartIso).getTime();
                const lastWeekStart = weekStart - 7 * 24 * 60 * 60 * 1000;

                const thisWeekRuns = screeningResultsForGroup.filter(
                  (result) => {
                    if (!result?.screening_timestamp) {
                      return false;
                    }
                    const timestamp = new Date(
                      result.screening_timestamp,
                    ).getTime();
                    return !Number.isNaN(timestamp) && timestamp >= weekStart;
                  },
                ).length;

                const lastWeekRuns = screeningResultsForGroup.filter(
                  (result) => {
                    if (!result?.screening_timestamp) {
                      return false;
                    }
                    const timestamp = new Date(
                      result.screening_timestamp,
                    ).getTime();
                    return (
                      !Number.isNaN(timestamp) &&
                      timestamp >= lastWeekStart &&
                      timestamp < weekStart
                    );
                  },
                ).length;

                return thisWeekRuns >= lastWeekRuns;
              })(),
            }
          : card,
      ),
    [
      complianceHealthStats?.current_score,
      complianceHealthStats?.change_pct_points,
      stats?.total_compliance_score_products,
      stats?.products_growth_pct,
      stats?.total_tariffs,
      aceFindingsTotal,
      totalHighRiskPagination?.totalItems,
      weeklyHighRiskPagination?.totalItems,
      screeningResultsForGroup,
      weekStartIso,
    ],
  );

  const flaggedEntitiesTotal = useMemo(
    () =>
      Number(stats?.total_quarantined_entities ?? 0) +
      Number(stats?.total_barred_entities ?? 0),
    [stats?.total_barred_entities, stats?.total_quarantined_entities],
  );

  const dynamicSignals = useMemo(
    () =>
      signals.map((signal) =>
        signal.title === 'Entities Flagged This Week'
          ? {
              ...signal,
              value: flaggedEntitiesTotal.toLocaleString(),
              footer:
                stats?.flagged_entities_this_week === null
                  ? '0'
                  : Number(stats?.flagged_entities_this_week) > 0
                  ? `+${Number(
                      stats?.flagged_entities_this_week ?? 0,
                    ).toLocaleString()} this week`
                  : `+${Number(
                      stats?.flagged_entities_last_week ?? 0,
                    ).toLocaleString()} from last week`,
            }
          : signal,
      ),
    [
      flaggedEntitiesTotal,
      stats?.flagged_entities_last_week,
      stats?.flagged_entities_this_week,
    ],
  );

  useEffect(() => {
    const fetchSavedWorkTickets = async () => {
      const groupId = cur_user?.selected_team?.id;
      const userId = cur_user?.basics?.id;
      const email = cur_user?.basics?.email;

      if (!groupId || !userId) {
        setSavedWorkTickets([]);
        return;
      }

      const mongoReq = {
        query: {
          groupId: groupId,
          // Read on the entity record's Deep Dive, not in Saved Work.
          docType: { $ne: 'Beneficial Ownership' },
          $or: [
            { sharedWith: userId },
            ...(email ? [{ sharedWith: email }] : []),
            { createdBy: userId },
          ],
        },
        projection: {
          _id: 1,
          ticketName: 1,
          createdAt: 1,
          lastUpdate: 1,
          docType: 1,
          tariffJurisdiction: 1,
          status: 1,
          frequency: 1,
          curTotal: 1,
          curNew: 1,
          entity: 1,
          commodity: 1,
          classificationValue: 1,
          eccnResults: 1,
          aiSums: 1,
          workflowData: 1,
          activityType: 1,
          workflowType: 1,
          results: 1,
          tariffResults: 1,
        },
      };

      const resp = await CasesAPI.searchCases(mongoReq);
      if (resp?.success && Array.isArray(resp?.result)) {
        setSavedWorkTickets(resp.result);
      } else {
        setSavedWorkTickets([]);
      }
    };

    fetchSavedWorkTickets();
  }, [
    cur_user?.selected_team?.id,
    cur_user?.basics?.id,
    cur_user?.basics?.email,
  ]);

  useEffect(() => {
    const fetchTariffAnalyzerSummaries = async () => {
      const recentTariffAnalyzers = (allDatabases || [])
        .filter((db: any) => db?.type === 'tariffs' && db?.id)
        .sort((a: any, b: any) => {
          const left = new Date(
            a?.created_at || a?.last_updated || 0,
          ).getTime();
          const right = new Date(
            b?.created_at || b?.last_updated || 0,
          ).getTime();
          return right - left;
        })
        .slice(0, 5);

      if (recentTariffAnalyzers.length === 0) {
        setTariffAnalyzerSummaries({});
        return;
      }

      const ids = recentTariffAnalyzers.map((db: any) => String(db.id));
      const summaries = await getTariffDatabaseSummariesBatch(ids);

      const summaryEntries = recentTariffAnalyzers.map((db: any) => {
        const databaseId = String(db.id);
        const s = summaries[databaseId];
        return [
          databaseId,
          {
            totalValue: Number(s?.total_value ?? 0),
            totalDuty: Number(s?.total_duty ?? 0),
            requiresReview: Number(s?.total_not_reviewed ?? 0),
          },
        ] as const;
      });

      setTariffAnalyzerSummaries(Object.fromEntries(summaryEntries));
    };

    fetchTariffAnalyzerSummaries();
  }, [allDatabases]);

  useEffect(() => {
    const fetchDatabaseSummaries = async () => {
      const recentDatabases = (allDatabases || [])
        .filter((db: any) => db?.type !== 'tariffs' && db?.id)
        .sort((a: any, b: any) => {
          const left = new Date(
            a?.created_at || a?.last_updated || 0,
          ).getTime();
          const right = new Date(
            b?.created_at || b?.last_updated || 0,
          ).getTime();
          return right - left;
        });

      if (recentDatabases.length === 0) {
        setDatabaseSummaries({});
        return;
      }

      const ids = recentDatabases.map((db: any) => String(db.id));
      const statsByDb = await getDatabaseStatsBatch(ids);

      const fulfilledEntries = recentDatabases.map((db: any) => {
        const databaseId = String(db.id);
        const s = statsByDb[databaseId];

        if (db?.type === 'products') {
          return [
            databaseId,
            {
              flaggedEntities: 0,
              reviewQueueEntities: 0,
              totalComplianceScore: Number(
                s?.total_compliance_score_products ?? 0,
              ),
              totalIssues: Number(s?.total_issues_products ?? 0),
              totalScreeningResults: Number(s?.total_screening_results ?? 0),
            },
          ] as const;
        }

        if (db?.type !== 'people & companies') {
          return [
            databaseId,
            {
              flaggedEntities: 0,
              reviewQueueEntities: 0,
              totalComplianceScore: 0,
              totalIssues: 0,
              totalScreeningResults: Number(s?.total_screening_results ?? 0),
            },
          ] as const;
        }

        return [
          databaseId,
          {
            flaggedEntities: Number(
              (s?.total_quarantined_entities ?? 0) +
                (s?.total_barred_entities ?? 0),
            ),
            reviewQueueEntities: Number(
              (s?.total_new_entities ?? 0) +
                (s?.total_not_reviewed_entities ?? 0),
            ),
            totalComplianceScore: 0,
            totalIssues: 0,
            totalScreeningResults: Number(s?.total_screening_results ?? 0),
          },
        ] as const;
      });

      setDatabaseSummaries(Object.fromEntries(fulfilledEntries));
    };

    fetchDatabaseSummaries();
  }, [allDatabases]);

  const dynamicActivityRows = useMemo(() => {
    const TOOL_DOC_TYPES = new Set([
      'Person / Company',
      'Import Product Screen',
      'Export Product Screen',
      'Multiple Entries',
      'Tariff',
      'Classification',
      'ECCN',
      'Control Checker',
      'ECCN Check',
    ]);

    const parseTimestamp = (value: string | undefined) => {
      if (!value) return 0;
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const recentActivities = recentActivity
      ? recentActivity.map((item: any) => {
          return {
            title: item.activityname,
            label: item.activitytype,
            iconKey: item.activitytype,
            detail: formatDBDetail(item),
            status: item.status,
            statusTone: getActivityStatusTone(item.status),
            time: item.activityDate
              ? getDateDifference(item.activitydate)
              : 'Recently',
            viewTo:
              item.activitytype === 'Product DB' ||
              item.activitytype === 'Entity DB'
                ? `/database/${item.typeid}`
                : item.activitytype === 'Ace Analyzer'
                ? `/ace-analyzers/${item.typeid}`
                : `/tariff-analyzers/${item.typeid}`,
            timestamp: parseTimestamp(item.activitydate),
          };
        })
      : [];

    const ticketActivity = (savedWorkTickets || []).map((ticket: any) => {
      const createdAt = ticket?.lastUpdate || ticket?.createdAt;
      const docType = ticket?.docType;
      const workflowTicket = isWorkflowTicket(ticket);
      const status = ticket?.status || 'Completed';
      const label = getActivityLabel({
        docType: docType,
        workflowType: ticket?.workflowType,
      });

      return {
        title: ticket?.ticketName || 'Saved Work Ticket',
        label: label,
        iconKey: label,
        detail: workflowTicket
          ? formatWorkflowDetail(ticket)
          : TOOL_DOC_TYPES.has(docType)
          ? formatToolDetail(ticket)
          : 'Ticket created',
        status: status,
        statusTone: getActivityStatusTone(status),
        time: createdAt ? getDateDifference(createdAt) : 'Recently',
        viewTo: ticket?._id ? `/monitor/ticket/${ticket._id}` : undefined,
        timestamp: parseTimestamp(createdAt),
      };
    });

    return [...recentActivities, ...ticketActivity]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 25)
      .map(({ timestamp: _timestamp, ...row }) => row);
  }, [savedWorkTickets, recentActivity]);

  return (
    <NewHomePageLayout title={'Dashboard'} user={cur_user.basics?.firstName}>
      {(requiresTeamOnboarding(cur_user) ||
        isMissingActiveTeamSelection(cur_user) ||
        showTrialReadyStep) && (
        <Modal isOpen={true} ariaHideApp={false}>
          <LoginWizard
            cur_user={cur_user}
            onTeamCreated={() => setShowTrialReadyStep(true)}
          />
        </Modal>
      )}

      <div className='home-v3'>
        <header className='home-v3-hero'>
          <div className='home-v3-welcome'>
            <div>
              <h1>Welcome back, {cur_user.basics?.firstName || 'User'}</h1>
            </div>
          </div>

          {/* <button type='button' className='home-v3-start-btn'>
            <AddIcon fontSize='small' />
            <span>Start New</span>
            <span className='home-v3-caret'>⌄</span>
          </button> */}
        </header>

        <TrialNoticeBanner
          key={cur_user?.selected_team?.id}
          cur_user={cur_user}
        />

        <section className='home-v3-section'>
          <div className='home-v3-section-head'>
            <div>
              <h2>Dashboard</h2>
              <p>Trade compliance overview at a glance.</p>
            </div>
          </div>

          <div className='home-v3-kpi-grid'>
            {dynamicKpiCards.map((card) => (
              <article
                className='home-v3-card home-v3-kpi-card'
                key={card.title}
              >
                <div className={`home-v3-pill home-v3-pill-${card.iconTone}`}>
                  {card.icon}
                </div>
                <div className='home-v3-kpi-value'>{card.value}</div>
                <div className='home-v3-kpi-title'>{card.title}</div>
                {/* <div
                  className={`home-v3-kpi-delta ${
                    card.positive ? 'is-up' : 'is-down'
                  }`}
                >
                  {card.positive ? '↗' : '↘'} {card.delta}
                </div> */}
              </article>
            ))}
          </div>
        </section>

        <SearchComp />

        <section className='home-v3-section'>
          <div className='home-v3-section-head'>
            <div>
              <h2>Databases</h2>
              <p>Your system-of-record for products and entities.</p>
            </div>
            {/* <Link to='/bite-db' className='home-v3-head-link'>
              Manage databases →
            </Link> */}
          </div>

          <div className='home-v3-db-grid'>
            {dynamicDatabaseCards.map((db) => (
              <article className='home-v3-card home-v3-db-card' key={db.title}>
                <div className='home-v3-db-top'>
                  <div className='home-v3-pill home-v3-pill-blue'>
                    {db.tab === 'Products' ? (
                      <Inventory2OutlinedIcon fontSize='small' />
                    ) : (
                      <GroupOutlined fontSize='small' />
                    )}
                  </div>
                  <div>
                    <h3>{db.title}</h3>
                    <p>{db.description}</p>
                  </div>
                </div>

                <div className='home-v3-db-mid'>
                  <div>
                    <div className='home-v3-db-total'>{db.total}</div>
                    <div className='home-v3-db-label'>{db.totalLabel}</div>
                  </div>
                  <div className='home-v3-quality-wrap'>
                    <div className='home-v3-quality-head'>
                      <span>{db.qualityLabel}</span>
                      <strong>{db.quality}%</strong>
                    </div>
                    <div className='home-v3-quality-track'>
                      <div
                        className='home-v3-quality-fill'
                        style={{ width: `${db.quality}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className='home-v3-db-foot'>
                  {/* <div className='home-v3-db-growth'>
                    <span>{db.delta}</span>
                    <em>{db.growth}</em>
                  </div> */}
                  {db.tab === 'People & Companies' && (
                    <Link
                      to={{
                        pathname:
                          db.tab === 'People & Companies'
                            ? '/bite-db/entities'
                            : '/bite-db/products',
                        state: { linkTab: db.tab },
                      }}
                    >
                      + Add Entities
                    </Link>
                  )}
                  {db.tab !== 'People & Companies' && (
                    <>
                      <Link
                        to={{
                          pathname: '/import-hub/databases',
                          state: { linkTab: db.tab },
                        }}
                      >
                        + Add Imports
                      </Link>
                      <Link
                        to={{
                          pathname: '/export-hub/databases',
                          state: { linkTab: db.tab },
                        }}
                      >
                        + Add Exports
                      </Link>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className='home-v3-section'>
          <div className='home-v3-section-head'>
            <div>
              <h2>Start Something</h2>
              <p>
                Run a tool, analyzer, or workflow. Outputs save to your
                workspace by default.
              </p>
            </div>
          </div>

          <div className='home-v3-start-grid'>
            {startCards.map((card) => (
              <article
                className='home-v3-card home-v3-start-card'
                key={card.title}
              >
                <div className='home-v3-start-head'>
                  <div className={`home-v3-pill home-v3-pill-${card.iconTone}`}>
                    {card.icon}
                  </div>
                  <div>
                    <h3>{card.title}</h3>
                    <p>{card.subtitle}</p>
                  </div>
                </div>

                <div
                  className={`home-v3-start-links ${
                    card.items.length > 4 ? 'two-columns' : ''
                  }`}
                >
                  {card.items.map((item) =>
                    item.disabled ? (
                      <span
                        key={item.label}
                        className='home-v3-start-link-item is-disabled'
                      >
                        → {item.label}
                      </span>
                    ) : (
                      <Link
                        to={item.to}
                        key={item.label}
                        className='home-v3-start-link-item'
                      >
                        → {item.label}
                      </Link>
                    ),
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className='home-v3-bottom-grid'>
          <article className='home-v3-activity'>
            <div className='home-v3-section-head'>
              <div>
                <h2>Recent Activity</h2>
                <p>Your latest runs, tools, and workflow executions.</p>
              </div>
              {/* <Link to='/bite-monitor' className='home-v3-head-link'>
                View all →
              </Link> */}
            </div>

            <div className='home-v3-card home-v3-table'>
              <div className='home-v3-table-head'>
                <span>Run</span>
                <span>Status</span>
                <span>Time</span>
              </div>
              {dynamicActivityRows.length > 0 ? (
                dynamicActivityRows.slice(0, 5).map((row) => (
                  <div
                    className='home-v3-table-row'
                    key={`${row.label}-${row.title}-${row.time}`}
                  >
                    <div className='home-v3-row-run'>
                      <div className='home-v3-row-dot'>
                        {getActivityIcon(row.iconKey)}
                      </div>
                      <div>
                        {row.viewTo ? (
                          <Link
                            to={row.viewTo}
                            className='home-v3-row-title-link'
                          >
                            <h4 className='home-v3-row-title'>{row.title}</h4>
                          </Link>
                        ) : (
                          <h4 className='home-v3-row-title'>{row.title}</h4>
                        )}
                        <p>
                          <span className='home-v3-row-label'>{row.label}</span>
                          {' · '}
                          {row.detail}
                        </p>
                      </div>
                    </div>
                    <div>
                      <span
                        className={`home-v3-status ${
                          statusClass[row.statusTone]
                        }`}
                      >
                        {row.status}
                      </span>
                    </div>
                    <div className='home-v3-row-time'>◷ {row.time}</div>
                  </div>
                ))
              ) : (
                <div className='home-v3-table-row home-v3-table-row-empty'>
                  <div className='home-v3-row-run home-v3-row-run-empty'>
                    <div>
                      <h4 className='home-v3-row-title'>No activities found</h4>
                      <p>No recent runs, tools, or workflow activity yet.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </article>

          <article className='home-v3-signals'>
            <div className='home-v3-section-head'>
              <div>
                <h2>Compliance Signals</h2>
                <p>Live intelligence across your trade data.</p>
              </div>
            </div>

            <div className='home-v3-signal-grid'>
              {dynamicSignals.map((signal) => (
                <div
                  className='home-v3-card home-v3-signal-card'
                  key={signal.title}
                >
                  <div
                    className={`home-v3-pill home-v3-pill-${signal.iconTone}`}
                  >
                    {signal.icon}
                  </div>
                  <div className='home-v3-signal-value'>{signal.value}</div>
                  <h3>{signal.title}</h3>
                  <p>{signal.subtitle}</p>
                  {/* <div className={signalFooterClass[signal.footerTone]}>
                    {signal.footer}
                  </div>
                  {signal.title.includes('Rulings') && (
                    <ArrowOutwardOutlinedIcon
                      className='home-v3-signal-arrow'
                      fontSize='small'
                    />
                  )} */}
                </div>
              ))}
            </div>
          </article>
        </section>
      </div>
    </NewHomePageLayout>
  );
};

export default NewHomeDashboardPage;
